// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * CSV formula-injection guard (OWASP "CSV Injection"; eng-brain P72-2).
 *
 * A spreadsheet runs any cell that starts with = + - @ (or a tab / CR) as a
 * formula, and CSV quoting does not stop it. Exported text an owner controls
 * (their device's name, a metric's notes, both writable through the API) could
 * therefore reach the vet's Excel as `=HYPERLINK(...)` or a DDE payload.
 *
 * Text cells that start with one of those characters get a leading apostrophe,
 * which spreadsheets show as text. Numbers and numeric strings ("-3", "-12.5")
 * are left alone, so no exported value changes meaning.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/
const NUMERIC = /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/

export const neutralizeCsvFormula = <T>(value: T): T | string =>
  typeof value === 'string' && FORMULA_LEAD.test(value) && !NUMERIC.test(value)
    ? `'${value}`
    : value

/** Apply the guard to every string leaf of a record (jsonexport flattens nested objects). */
export const neutralizeCsvRecord = (value: unknown): any => {
  if (Array.isArray(value)) return value.map(neutralizeCsvRecord)
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, neutralizeCsvRecord(v)]),
    )
  }
  return neutralizeCsvFormula(value)
}
